import React, { useEffect, useRef, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Autocomplete } from "../components/Autocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { UserPlus, Users, Upload, FileDown, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { askReason } from "../components/ApprovalLock";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

export default function Parties() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", account_name: "", phone: "", email: "", vehicle_number: "", guarantor_name: "", guarantor_phone: "", guarantor_account: "", credit_limit: "", opening_balance: "", opening_date: "" });
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const load = async () => setParties((await api.get("/parties")).data);
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? parties.filter((p) => [p.name, p.account_name, p.phone, p.email, p.vehicle_number, p.guarantor_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : parties;

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/parties/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} added, ${data.updated} updated`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) skipped — check names`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = () => {
    const csv = "name,phone,email,credit_limit,opening_balance,opening_date\nRajesh Traders,9876543210,rajesh@example.com,50000,12500,2026-04-01\nSharma Auto,,,0,8000,2026-04-01\n";
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "parties_opening_balance_template.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const openNew = () => { setEdit(null); setForm({ name: "", account_name: "", phone: "", email: "", vehicle_number: "", guarantor_name: "", guarantor_phone: "", guarantor_account: "", credit_limit: "", opening_balance: "", opening_date: "" }); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setForm({ name: p.name, account_name: p.account_name || "", phone: p.phone || "", email: p.email || "", vehicle_number: p.vehicle_number || "", guarantor_name: p.guarantor_name || "", guarantor_phone: p.guarantor_phone || "", guarantor_account: p.guarantor_account || "", credit_limit: p.credit_limit || "", opening_balance: p.opening_balance || "", opening_date: p.opening_date || "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, credit_limit: parseFloat(form.credit_limit) || 0, opening_balance: parseFloat(form.opening_balance) || 0 };
      if (edit) {
        const edit_reason = askReason(user, "editing", `party ${edit.name}`);
        if (edit_reason === null) return;
        const res = await api.put(`/parties/${edit.id}`, { ...payload, edit_reason });
        if (res.data?.status === "pending") { toast.info(res.data.message); setOpen(false); return; }
      } else {
        if (!payload.name) return toast.error("Name required");
        await api.post("/parties", payload);
      }
      toast.success(edit ? "Party updated" : "Party added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete party "${p.name}"? It has no transactions. This cannot be undone.`)) return;
    try { await api.delete(`/parties/${p.id}`); toast.success("Party deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Parties" subtitle="Customers & vendors — set credit limits and opening balances here.">
          {canWrite && (
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="party-import-input" />
              <button data-testid="party-template-btn" onClick={downloadTemplate}
                className="h-10 px-3 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
                <FileDown size={15} /> Template
              </button>
              <button data-testid="party-import-btn" onClick={() => fileRef.current?.click()}
                className="h-10 px-3 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
                <Upload size={15} /> Import CSV
              </button>
              <button data-testid="add-party-btn" onClick={openNew}
                className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                <UserPlus size={16} /> Add Party
              </button>
            </div>
          )}
        </PageHeader>

        <div className="relative mb-5 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="party-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, account, phone, email or vehicle…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
        </div>

        {parties.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 opacity-40" size={32} /> No parties yet.
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="party-no-results" className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            No parties match “{search}”.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Party</span><span className="text-right">Credit Limit</span><span className="text-right">Outstanding</span><span className="w-8" />
            </div>
            <div className="divide-y divide-border">
              {filtered.map((p) => (
                <div key={p.id} onClick={() => canWrite && openEdit(p)} data-testid={`party-row-${p.id}`}
                  className={`grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-4 items-center transition-colors ${canWrite ? "cursor-pointer hover:bg-background/50" : ""}`}>
                  <div>
                    <div className="font-medium">{p.name}{p.opening_balance > 0 && <span className="ml-2 text-[11px] text-accent">opening ₹{Number(p.opening_balance).toLocaleString("en-IN")}</span>}</div>
                    <div className="text-xs text-muted-foreground num">{p.phone || "—"} · {p.email || "—"}{p.vehicle_number ? ` · ${p.vehicle_number}` : ""}</div>
                  </div>
                  <span className="num text-sm text-right">{p.credit_limit > 0 ? inr(p.credit_limit) : "—"}</span>
                  <span className={`num text-sm text-right font-semibold ${p.over_limit ? "text-destructive" : ""}`}>{inr(p.outstanding)}</span>
                  <div className="w-8 flex justify-end" onClick={(e) => e.stopPropagation()}>
                    {canWrite && !p.has_transactions && (
                      <button data-testid={`party-delete-${p.id}`} onClick={(e) => del(p, e)} title="Delete party (no transactions)"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className={label}>Name</label>
                <input data-testid="party-name" className={field} value={form.name} disabled={!!edit}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Party name" />
              </div>
              <div>
                <label className={label}>Account name <span className="text-muted-foreground font-normal">(ledger / account holder name)</span></label>
                <input data-testid="party-account-name" className={field} value={form.account_name}
                  onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="e.g. M/s Rajesh Traders A/c" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Phone</label>
                  <input data-testid="party-phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><label className={label}>Email</label>
                  <input data-testid="party-email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div>
                <label className={label}>Vehicle number</label>
                <input data-testid="party-vehicle" className={field} value={form.vehicle_number}
                  onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} placeholder="DL01AB1234 (optional)" />
              </div>
              <div className="rounded-md border border-primary/15 bg-primary/5 p-3">
                <div className="text-sm font-medium mb-2">Guarantor details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Guarantor name</label>
                    <input data-testid="party-guarantor-name" className={field} value={form.guarantor_name}
                      onChange={(e) => setForm({ ...form, guarantor_name: e.target.value })} placeholder="optional" /></div>
                  <div><label className={label}>Guarantor phone</label>
                    <input data-testid="party-guarantor-phone" className={field} value={form.guarantor_phone}
                      onChange={(e) => setForm({ ...form, guarantor_phone: e.target.value })} placeholder="optional" /></div>
                </div>
                <div className="mt-3"><label className={label}>Guarantor account</label>
                  <input data-testid="party-guarantor-account" className={field} value={form.guarantor_account}
                    onChange={(e) => setForm({ ...form, guarantor_account: e.target.value })} placeholder="Account name / number (optional)" /></div>
              </div>
              <div>
                <label className={label}>Credit limit (₹) — reminder triggers above this</label>
                <input data-testid="party-limit" type="number" className={field} value={form.credit_limit}
                  onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder="0 = no limit" />
              </div>
              <div className="rounded-md border border-accent/20 bg-accent/5 p-3">
                <div className="text-sm font-medium mb-2">Opening balance (migrating old ledger)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Amount already owed (₹)</label>
                    <input data-testid="party-opening-balance" type="number" className={field} value={form.opening_balance}
                      onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} placeholder="0" /></div>
                  <div><label className={label}>As of date</label>
                    <input data-testid="party-opening-date" type="date" className={`${field} num`} value={form.opening_date}
                      onChange={(e) => setForm({ ...form, opening_date: e.target.value })} /></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">This amount is added to the party's outstanding and shows as the first line in their ledger. Payments received (Bulk mode) settle it automatically.</p>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="party-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
